import { darkTheme, keyframes, styled } from "~/ui/style/stitches.config";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import {
  createPortalSession,
  getPlusSubscription,
  getSubscriptionStatus,
  getPlusPrices,
  type PlusPrices,
} from "~/api/paymentsApi";
import { formatCurrency } from "~/localization/formatPrice";
import {
  resolveCurrency,
  detectCountryFromTimezone,
} from "~/localization/currencyMap";
import { isInExtendedTrial } from "~/api/userApi";
import { Button } from "~/ui/components/Button";
import { PlusBadge } from "~/ui/menus/BackgroundMenu";
import { AuthContext } from "~/utils/useAuth";
import { HelpButton } from "~/ui/components/HelpButton";
import { SimpleLoadingIndicator } from "../loading/LoadingPage";
import { trackEvent } from "~/api/analyticsApi";
import { FAQ, FAQItemProps } from "~/ui/components/FAQ";
import { BillingOverlay } from "./BillingOverlay";
import { isIOSNativeApp } from "~/utils/deviceDetection";
import { Gradient } from "~/ui/components/Gradient";
import { useSnapshot } from "valtio";
import { localState } from "~/state/state";
import { t } from "~/localization/translate";

export const Success = () => {
  useEffect(() => {
    const postPaymentRedirect = localStorage.getItem('postPaymentRedirect');
    if (postPaymentRedirect) {
      localStorage.removeItem('postPaymentRedirect');
      window.location.href = postPaymentRedirect;
    }
  }, []);

  return (
    <StyledSuccessPage>
      <div className="success-container">
        <h1>Thanks for subscribing! ✨</h1>
        <p>
          Now you're ready to create tarot rooms whenever inspiration strikes,
          host friends for group readings, or reflect deeply when you need
          clarity.
        </p>
        <div style={{ display: "flex", justifyContent: "center", gap: "10px" }}>
          <button
            onClick={() => (window.location.href = "/")}
            className="continue-button"
            style={{
              width: "150px",
              height: "45px",
              color: "white",
              backgroundColor: "black",
              border: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: "400",
              fontSize: "13px",
              cursor: "pointer",
            }}
          >
            Return home
          </button>
          <button
            onClick={() => (window.location.href = "/create-room")}
            className="continue-button"
            style={{
              width: "150px",
              height: "45px",
              color: "black",
              backgroundColor: "white",
              border: "1px solid black",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: "400",
              fontSize: "13px",
              cursor: "pointer",
            }}
          >
            Open a room
          </button>
        </div>
      </div>
    </StyledSuccessPage>
  );
};

const StyledSuccessPage = styled("div", {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  flexDirection: "column",
  height: "100vh",
  backgroundColor: "$wash",
  color: "$text",
  [`.${darkTheme.className} &`]: {
    backgroundColor: "$appBg",
  },
  // buttons use inline styles, so dark overrides need !important
  "& .continue-button:first-child": {
    [`.${darkTheme.className} &`]: {
      backgroundColor: "#fff !important",
      color: "#000 !important",
    },
  },
  "& .continue-button:last-child": {
    [`.${darkTheme.className} &`]: {
      backgroundColor: "transparent !important",
      color: "#fff !important",
      border: "1px solid rgba(255, 255, 255, 0.4) !important",
    },
  },
  "& .continue-button": {
    transition: "all 0.2s ease-in-out",
    boxShadow: "rgb(213, 181, 253) 0px 0px 10px 1px",
    borderRadius: "10px",
    "&:hover": {
      boxShadow: "rgb(213, 181, 253) 0px 0px 15px 3px",
      opacity: ".8 !important",
    },
  },
  "& .success-container": {
    marginTop: "-16px",
    backgroundColor: "$surface",
    boxShadow: "0px 3px 0px #000000",
    border: "1px solid $border",
    [`.${darkTheme.className} &`]: {
      boxShadow: "0px 3px 0px rgba(255, 255, 255, 0.3)",
    },
    borderRadius: "36px",
    width: "450px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    height: "270px",

    "& h1": {
      width: "321px",
      textAlign: "center",
      fontSize: "16px",
      fontWeight: "500",
    },
    "& p": {
      marginBottom: "42px",
      marginTop: "18px",
      width: "321px",
      textAlign: "center",
      fontSize: "13px",
      lineHeight: "1.4",
    },
  },

  "@mobile": {
    "& p": {
      width: "90%!important",
      height: "auto",
    },
    "& .success-container": {
      width: "90%!important",
      height: "auto",
      padding: "30px",
      borderRadius: "24px",
    },
  },
});

const Feature = ({
  header,
  text,
  img,
  isPlus,
}: {
  header?: string;
  text: string;
  img: string;
  isPlus?: boolean;
}) => {
  return (
    <div className="feature">
      <div className="feature-line">
        <img src={img} />
        <div className="feature-header">{header}</div>
        <div>{text}</div>
      </div>
      {isPlus ? (
        <PlusBadge
          style={{
            boxShadow: "none",
            paddingTop: "1px",
            color: "white",
            backgroundColor: "#535353",
          }}
          className="plus"
        >
          NEW
        </PlusBadge>
      ) : (
        <div></div>
      )}
    </div>
  );
};

const featureListItems = [
  t("billing.featureList.unlimited"),
  t("billing.featureList.keywords"),
  t("billing.featureList.guidebook"),
  t("billing.featureList.journaling"),
  t("billing.featureList.spreads"),
  t("billing.featureList.friends"),
  t("billing.featureList.decks"),
];

const FeatureList = () => (
  <StyledFeatureList>
    {featureListItems.map((item, idx) => (
      <div className="feature-item" key={idx}>
        <div className="check-circle">
          <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
            <path d="M3 7L6 10L11 4" stroke="white" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        <span>{item}</span>
      </div>
    ))}
  </StyledFeatureList>
);

const StyledFeatureList = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  marginTop: "0px",
  marginBottom: "32px",
  padding: "0 20px",
  maxWidth: "400px",

  "& .feature-item": {
    display: "flex",
    alignItems: "flex-start",
    gap: "12px",
    fontSize: "13px",
    fontWeight: "400",
    lineHeight: "1.4",
  },

  "& .check-circle": {
    width: "20px",
    height: "20px",
    minWidth: "20px",
    borderRadius: "50%",
    backgroundColor: "#1a1a1a",
    [`.${darkTheme.className} &`]: {
      backgroundColor: "$inverseSurface",
      "& path": { stroke: "#000" },
    },
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginTop: "0px",
  },
});

const toggleOptionStyle: React.CSSProperties = {
  // color comes from .billing-toggle-option styles (theme aware)
  flex: 1,
  padding: "5px 0",
  fontSize: "11px",
  textTransform: "lowercase",
  fontWeight: "400",
  cursor: "pointer",
  color: "#232323",
  position: "relative",
  zIndex: 1,
  textAlign: "center",
  userSelect: "none",
};

const BillingToggle = ({
  billingPeriod,
  setBillingPeriod,
}: {
  billingPeriod: 'monthly' | 'annual';
  setBillingPeriod: (period: 'monthly' | 'annual') => void;
}) => (
  <div
    className="billing-toggle"
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginBottom: "30px",
      position: "relative",
      backgroundColor: "transparent",
      border: "1px solid #ddd",
      borderRadius: "12px",
      padding: "4px",
      width: "100%",
      maxWidth: "180px",
    }}
  >
    {/* Sliding highlight */}
    <div
      className="billing-toggle-highlight"
      style={{
        position: "absolute",
        top: "3px",
        left: billingPeriod === 'monthly' ? "3px" : "calc(50% - 1px)",
        width: "calc(50% - 3px)",
        height: "calc(100% - 6px)",
        backgroundColor: "#eaeaea",
        borderRadius: "8px",
        transition: "left 0.2s ease",
      }}
    />
    <div onClick={() => { setBillingPeriod('monthly'); trackEvent("/plans/toggled_billing", { period: 'monthly' }); }} style={toggleOptionStyle}>
      {t("billing.toggleMonthly")}
    </div>
    <div onClick={() => { setBillingPeriod('annual'); trackEvent("/plans/toggled_billing", { period: 'annual' }); }} style={toggleOptionStyle}>
      {t("billing.toggleAnnual")}
    </div>
  </div>
);

const PlanInfo = ({
  description,
  isPro,
  isAppStoreSubscriber,
  isPlayStoreSubscriber,
  isSubscribed,
  renewalDate,
  cancelAtPeriodEnd,
  subscribeButton,
  billingPeriod,
  setBillingPeriod,
  plusPrices,
}: {
  description: string;
  isPro?: boolean;
  isAppStoreSubscriber?: boolean;
  isPlayStoreSubscriber?: boolean;
  isSubscribed?: boolean;
  renewalDate?: string | null;
  cancelAtPeriodEnd?: boolean;
  subscribeButton?: React.ReactNode;
  billingPeriod: 'monthly' | 'annual';
  setBillingPeriod: (period: 'monthly' | 'annual') => void;
  plusPrices?: PlusPrices | null;
}) => (
  <div className="plan-info">
    <div className="pricing-box">
      {isAppStoreSubscriber ? (
        <span style={{ fontSize: "13px", fontWeight: "500", paddingTop: "14px", textAlign: "center" }}>
          {t("billing.subscribedThroughApple")}
        </span>
      ) : isPlayStoreSubscriber ? (
        <span style={{ fontSize: "13px", fontWeight: "500", paddingTop: "14px", textAlign: "center" }}>
          {t("billing.subscribedThroughGoogle")}
        </span>
      ) : isSubscribed && renewalDate ? (
        <div style={{ textAlign: "center", paddingTop: "14px", paddingBottom: "10px" }}>
          <span style={{ fontSize: "10px", fontWeight: "400", textTransform: "uppercase", letterSpacing: "0.03px", display: "block", marginBottom: "10px", opacity: 0.6 }}>
            {t("billing.youreSubscribed")}
          </span>
          <span style={{ fontSize: "13px", fontWeight: "400" }}>
            {cancelAtPeriodEnd ? t("billing.expires") : t("billing.renews")} {renewalDate}
          </span>
        </div>
      ) : !isIOSNativeApp() ? (
        <>
          <BillingToggle billingPeriod={billingPeriod} setBillingPeriod={setBillingPeriod} />
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "baseline",
              justifyContent: "center",
              gap: "4px",
              marginBottom: "10px",
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: "4px" }}>
                <span style={{ fontSize: "19px", fontWeight: "400" }}>
                  {plusPrices
                    ? billingPeriod === 'monthly'
                      ? formatCurrency(plusPrices.monthly.unitAmount, plusPrices.monthly.currency)
                      : formatCurrency(Math.round(plusPrices.annual.unitAmount / 12), plusPrices.annual.currency)
                    : ""}
                </span>
                <span style={{ fontSize: "13px", fontWeight: "400" }}>/ {t("billing.monthUnit")}</span>
              </div>
              <span style={{ fontSize: "11px", fontWeight: "400", opacity: billingPeriod === 'annual' ? 0.55 : 0, marginTop: "4px" }}>
                {plusPrices ? t("billing.billedAnnually", { price: formatCurrency(plusPrices.annual.unitAmount, plusPrices.annual.currency) }) : ""}
              </span>
            </div>
          </div>
        </>
      ) : null}

      {subscribeButton}

      <p
        dangerouslySetInnerHTML={{
          __html: isPro ? t("billing.legacyProPlan") : description,
        }}
      ></p>
    </div>
  </div>
);

export const BillingPage = () => {
  const { user } = useContext(AuthContext);
  const { appDarkMode } = useSnapshot(localState);
  const [_, setLocation] = useLocation();

  const handleCreatePortal = async () => {
    try {
      // Set a timeout to reset the loading state
      // This will run before the page redirects
      setTimeout(() => {
        setIsLoadingStripe(false);
      }, 800);

      await createPortalSession("/plans");
    } catch (error) {
      console.error("Stripe portal error");
      setIsLoadingStripe(false);
    }
  };

  const [isOnPlus, setIsOnPlus] = useState(false);
  const [isPro, setIsPro] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annual'>('monthly');

  const [isLoadingStripe, setIsLoadingStripe] = useState(false);
  const [hasLoadedPlanData, setHasLoadedPlanData] = useState(false);
  const [trialString, setTrialString] = useState<string>("");
  const [showStripeCheckout, setShowStripeCheckout] = useState(false);
  const [subscriptionSource, setSubscriptionSource] = useState<'STRIPE' | 'APP_STORE' | 'GOOGLE_PLAY' | null>(null);
  const [showAppStoreManagementModal, setShowAppStoreManagementModal] = useState(false);
  const [showPlayStoreManagementModal, setShowPlayStoreManagementModal] = useState(false);
  const [renewalDate, setRenewalDate] = useState<string | null>(null);
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(false);
  const [plusPrices, setPlusPrices] = useState<PlusPrices | null>(null);
  const [plusPricesError, setPlusPricesError] = useState(false);
  // Presentment currency is derived from the browser timezone (timezone ->
  // country -> currency, USD when unsupported). Computed once per page load;
  // there is no user-facing selector.
  const [currency] = useState<string>(() =>
    resolveCurrency(detectCountryFromTimezone())
  );

  // Only the most recent price request may update state, so a slow response
  // from an earlier request (e.g. a retry) can't overwrite a newer one.
  const latestPricesRequestId = useRef(0);

  const loadPlusPrices = useCallback(() => {
    const requestId = ++latestPricesRequestId.current;
    setPlusPrices(null);
    setPlusPricesError(false);
    return getPlusPrices(currency)
      .then((prices) => {
        if (requestId === latestPricesRequestId.current) setPlusPrices(prices);
      })
      .catch((error) => {
        if (requestId !== latestPricesRequestId.current) return;
        console.error("Failed to load plus prices", error);
        setPlusPricesError(true);
      });
  }, [currency]);

  useEffect(() => {
    loadPlusPrices();
  }, [loadPlusPrices]);

  useEffect(() => {
    // Only show trial string for users in trial (not subscribed, not pro)
    if (user && user.trialEndsAt && !user.isPlus && !user.isProfessional) {
      if (isInExtendedTrial(user)) {
        setTrialString(t("billing.trial.extended"));
        return;
      }

      const now = new Date();
      const trialEnd = new Date(user.trialEndsAt);
      const timeDiff = trialEnd.getTime() - now.getTime();

      const oneDay = 24 * 60 * 60 * 1000;
      const oneHour = 60 * 60 * 1000;
      const diffHours = Math.floor(timeDiff / oneHour);
      const diffDays = Math.ceil(timeDiff / oneDay);

      if (timeDiff > 0) {
        // Less than 1 hour
        if (diffHours === 0) {
          setTrialString(t("billing.trial.hourLeft"));
        // Less than 9 hours - show hours for urgency
        } else if (diffHours < 9) {
          setTrialString(
            diffHours === 1
              ? t("billing.trial.hourLeft")
              : t("billing.trial.hoursLeft", { count: diffHours })
          );
        } else if (diffDays > 0) {
          setTrialString(
            diffDays === 1
              ? t("billing.trial.dayLeft")
              : t("billing.trial.daysLeft", { count: diffDays })
          );
        } else {
          setTrialString("");
        }
      } else {
        setTrialString("");
      }
    } else {
      setTrialString("");
    }
  }, [user]);

  useEffect(() => {
    trackEvent("/plans/visited_page");
    (async function () {
      if (user) {
        try {
          const unified = await getSubscriptionStatus();
          setIsOnPlus(unified.plus.active);
          setSubscriptionSource(unified.plus.source);

          // Set renewal date and cancellation status if available
          if (unified.plus.active && unified.plus.expiresAt) {
            // expiresAt could be in seconds (Stripe) or milliseconds - check and handle both
            const timestamp = unified.plus.expiresAt > 9999999999
              ? unified.plus.expiresAt
              : unified.plus.expiresAt * 1000;
            const date = new Date(timestamp);
            setRenewalDate(date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }));
            setCancelAtPeriodEnd(unified.plus.cancelAtPeriodEnd ?? false);
          }
        } catch {
          // fallback to Stripe-only
          const subscriptionData = await getPlusSubscription();
          setIsOnPlus(user.isPlus);
          setSubscriptionSource('STRIPE'); // Default to Stripe for fallback

          // Set renewal date and cancellation status from legacy endpoint
          if (subscriptionData.status === "active" && subscriptionData.current_period_end) {
            const date = new Date(parseInt(subscriptionData.current_period_end) * 1000);
            setRenewalDate(date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }));
            setCancelAtPeriodEnd(subscriptionData.cancel_at_period_end ?? false);
          }
        }
        setHasLoadedPlanData(true);
        setIsPro(user.isProfessional);
      } else {
        // if no data after 1 second, show page anyways
        window.setTimeout(() => {
          setHasLoadedPlanData(true);
        }, 1000);
      }
    })();
  }, [user]);

  useEffect(() => {
    const handleCloseModal = () => {
      setShowStripeCheckout(false);
      setIsLoadingStripe(false);
    };

    document.addEventListener('closeBillingModal', handleCloseModal);

    return () => {
      document.removeEventListener('closeBillingModal', handleCloseModal);
    };
  }, []);

  useEffect(() => {
    if (showStripeCheckout) {
      // Prevent body scrolling when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [showStripeCheckout]);

  return (
    <StyledBillingPage modalActive={showStripeCheckout}>
      {!hasLoadedPlanData ? (
        <SimpleLoadingIndicator />
      ) : (
        <>

          {!isIOSNativeApp() && (
            <Button
              onClick={() => setLocation("/")}
              transparent
              className="home-button"
              style={{
                border: "none",
                position: "fixed",
                padding: "0 16px 0 14px",
                height: "40px",
                top: "67px",
                left: "46px",
                transition: "all 0.2s ease-in-out",
                display: "flex",
                alignItems: "center",
                fontSize: "12px",
                borderRadius: "1rem",
                zIndex: 999,
              }}
            >
              <span
                style={{
                  marginRight: "8px",
                  fontSize: "16px",
                  fontWeight: "200",
                }}
                className="arrow"
              >
                &larr;
              </span>
              <span className="text">{t("billing.home")}</span>
            </Button>
          )}

          <div className="plan-details">
            <h2>{t("billing.heading")}</h2>
          </div>
        <Gradient top="120px" dark={appDarkMode} />
          <HelpButton isOnBillingPage text={t("billing.contactUs")} />
          <div className="all-plans">
            <div className={`plan plus`} style={{ position: "relative" }}>
              <FeatureList />

              <PlanInfo
                description={trialString}
                isPro={isPro}
                isAppStoreSubscriber={subscriptionSource === 'APP_STORE'}
                isPlayStoreSubscriber={subscriptionSource === 'GOOGLE_PLAY'}
                isSubscribed={isOnPlus}
                renewalDate={renewalDate}
                cancelAtPeriodEnd={cancelAtPeriodEnd}
                billingPeriod={billingPeriod}
                setBillingPeriod={setBillingPeriod}
                plusPrices={plusPrices}
                subscribeButton={
                  !isIOSNativeApp() && (
                    <Button
                      style={{ fontSize: "14px", position: "relative", marginTop: "10px" }}
                      className="continue-button"
                      onClick={() => {
                        //if you're not signed in, redirect to sign in page which will then redirect back to plans page after you're done
                        if (!user) {
                          setLocation("/signup?dest=/plans");
                          return;
                        }
                        if (isOnPlus) {
                          if (isLoadingStripe) return;

                          if (subscriptionSource === 'APP_STORE') {
                            // Show modal for iOS users
                            setShowAppStoreManagementModal(true);
                            return;
                          }

                          if (subscriptionSource === 'GOOGLE_PLAY') {
                            // Show modal for Android users
                            setShowPlayStoreManagementModal(true);
                            return;
                          }

                          setIsLoadingStripe(true);
                          handleCreatePortal();
                        } else {
                          if (isLoadingStripe) return;
                          trackEvent("/plans/pressed_subscribe");
                          setIsLoadingStripe(false); // Reset loading state immediately
                          setShowStripeCheckout(true);
                        }
                      }}
                    >
                      {isLoadingStripe ? (
                        <SimpleLoadingIndicator
                          style={{
                            top: 0,
                            width: "30px",
                            height: "30px",
                            backgroundColor: "rgba(255, 255, 255, 0.3)",
                          }}
                        />
                      ) : isOnPlus ? (
                        t("billing.managePlan")
                      ) : !user ? (
                        t("billing.tryItFree")
                      ) : (
                        t("billing.subscribe")
                      )}
                    </Button>
                  )
                }
              />

              <StyledPress>
                <a
                  href="https://www.theguardian.com/technology/article/2024/aug/17/tarot-card-readers-impersonators-scammers"
                  target="_blank"
                >
                  <img
                    src="https://moonlight.world/cdn/shop/files/The_Guardian_logo.png?v=1724874894&width=500"
                    alt="The Guardian"
                  />
                </a>

                <a
                  href="https://news.artnet.com/art-world/danielle-baskin-moonlight-virtual-tarot-2438005"
                  target="_blank"
                >
                  <img
                    src="https://moonlight.world/cdn/shop/files/artnet_logo.png?v=1724874894&width=500"
                    alt="ArtNet"
                  />
                </a>

                <a
                  href="https://hyperallergic.com/833385/moonlight-tarot-platform-for-the-zoom-era/"
                  target="_blank"
                >
                  <img
                    src="https://moonlight.world/cdn/shop/files/hyperallergic_logo.png?v=1724874894&width=500"
                    alt="Hyperallergic"
                  />
                </a>

                <a
                  href="https://wildhunt.org/2024/06/moonlight-a-new-platform-for-tarot.html"
                  target="_blank"
                >
                  <img
                    src="https://moonlight.world/cdn/shop/files/wild_hunt_logo.png?v=1724874894&width=500"
                    alt="The Wild Hunt"
                  />
                </a>
              </StyledPress>

              <div className="plan-info" style={{ marginBottom: "-16px", marginTop: "50px" }}>
                <h1>{t("billing.featuresHeading")}</h1>
              </div>

              <div className="features">



                <Feature
                  header={t("billing.features.meaningsHeader")}
                  text={t("billing.features.meaningsText")}
                  img=""
                />


                <Feature
                  header={t("billing.features.anywhereHeader")}
                  text={t("billing.features.anywhereText")}
                  img=""
                />


                <Feature
                  header={t("billing.features.spreadsHeader")}
                  text={t("billing.features.spreadsText")}
                  img=""
                />


                <Feature
                  header={t("billing.features.deckArtHeader")}
                  text={t("billing.features.deckArtText")}
                  img=""
                />


                <Feature
                  header={t("billing.features.friendsHeader")}
                  text={t("billing.features.friendsText")}
                  img=""
                />

                <Feature
                  header={t("billing.features.practiceHeader")}
                  text={t("billing.features.practiceText")}
                  img=""
                />

                <span
                  className="plus-features"
                  style={{ position: "relative" }}
                ></span>
              </div>

            </div>
          </div>

          <BillingSenjaWidget />
          <ReviewSenjaWidget />

          {!isOnPlus && !isPro && (
            <StyledNotes>
              <p>
                {t("billing.extendTrialPrompt")}{" "}
                <a href="mailto:support@moonlight.world">{t("billing.sendANote")}</a>
              </p>
            </StyledNotes>
          )}

          <FAQ faqData={billingFaqData} />
        </>
      )}
      <HelpButton isOnBillingPage text={t("billing.contactUs")} />


      {showStripeCheckout && (
        <div
          className="billing-modal"
          onClick={(e) => {
            // Close when clicking the backdrop (but not when clicking the modal itself)
            if (e.target === e.currentTarget) {
              setShowStripeCheckout(false);
              setIsLoadingStripe(false);
            }
          }}
        >
          <BillingOverlay
            billingPeriod={billingPeriod}
            plusPrices={plusPrices}
            plusPricesError={plusPricesError}
            onRetryPrices={loadPlusPrices}
            onSuccess={(subscriptionId: string) => {
              console.log("successfully created Stripe subscription", subscriptionId);

              // Track the purchase event
              trackEvent("/plans/purchased_subscription");

              // Add a small delay to ensure tracking has time to complete
              setTimeout(() => {
                setShowStripeCheckout(false); // Close the modal on success
                setIsLoadingStripe(false);
                const postPaymentRedirect = localStorage.getItem('postPaymentRedirect');
                if (postPaymentRedirect) {
                  localStorage.removeItem('postPaymentRedirect');
                  window.location.href = postPaymentRedirect;
                } else {
                  window.location.href = window.location.origin + "/success";
                }
              }, 500); // Half second delay to ensure tracking completes
            }}
          />
        </div>
      )}

      {showAppStoreManagementModal && (
        <div
          className="billing-modal app-store-modal"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowAppStoreManagementModal(false);
            }
          }}
        >
          <StyledAppStoreModal>
            <img
              src="/images/room/x-icon.png"
              className="x-button"
              onClick={() => setShowAppStoreManagementModal(false)}
            />
            <h2>You're subscribed on iOS.</h2>
            <p>Manage in the App Store by going to your Settings &gt; AppleID &gt; Subscriptions and finding Moonlight. You can cancel or view your next billing date there.</p>
          </StyledAppStoreModal>
        </div>
      )}

      {showPlayStoreManagementModal && (
        <div
          className="billing-modal play-store-modal"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowPlayStoreManagementModal(false);
            }
          }}
        >
          <StyledAppStoreModal>
            <img
              src="/images/room/x-icon.png"
              className="x-button"
              onClick={() => setShowPlayStoreManagementModal(false)}
            />
            <h2>You're subscribed on Android.</h2>
            <p>Manage in the Play Store by opening the Google Play app, tapping your profile &gt; Payments &amp; subscriptions &gt; Subscriptions and finding Moonlight. You can cancel or view your next billing date there.</p>
          </StyledAppStoreModal>
        </div>
      )}

    </StyledBillingPage>
  );
};

const spin = keyframes({
  "0%": {
    transform: "rotate(0deg)",
  },
  "100%": {
    transform: "rotate(360deg)",
  },
});

const StyledSenjaWidget = styled("div", {
  "@mobile": {
    width: "90%",
    maskImage: "none !important",
    WebkitMaskImage: "none !important",
  },
});

export const BillingSenjaWidget = () => {
  useEffect(() => {
    const script = document.createElement("script");
    script.src =
      "https://widget.senja.io/widget/94cfc325-7696-4f38-9e7a-de7f177fb41a/platform.js";
    script.type = "text/javascript";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
      void 0;
    };
  }, []);

  return (
    <StyledSenjaWidget>
      <div
        className="senja-embed"
        data-id="94cfc325-7696-4f38-9e7a-de7f177fb41a"
        data-mode="shadow"
        data-lazyload="false"
        onClick={() => trackEvent("/plans/pressed_reviews")}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          marginTop: "12px",
          marginBottom: "12px",
          width: "100%",
        }}
      />
    </StyledSenjaWidget>
  );
};

export const ReviewSenjaWidget = () => {
  useEffect(() => {
    const script = document.createElement("script");
    script.src =
      "https://widget.senja.io/widget/3e99fa73-939a-427f-b631-a7efa7c835c3/platform.js";
    script.type = "text/javascript";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
      void 0;
    };
  }, []);

  return (
    <StyledSenjaWidget>
      <div
        className="senja-embed"
        data-id="3e99fa73-939a-427f-b631-a7efa7c835c3"
        data-mode="shadow"
        data-lazyload="false"
        style={{
          display: "block",
          width: "100%",
          maxWidth: "500px",
          margin: "0 auto",
          textAlign: "center",
          paddingTop: "50px",
          paddingBottom: "70px",
        }}
      />
    </StyledSenjaWidget>
  );
};

const billingFaqData: FAQItemProps[] = [
  { question: t("faq.q1"), answer: t("faq.a1") },
  { question: t("faq.q2"), answer: t("faq.a2") },
  { question: t("faq.q3"), answer: t("faq.a3") },
  { question: t("faq.q4"), answer: t("faq.a4") },
  { question: t("faq.q5"), answer: t("faq.a5") },
  { question: t("faq.q6"), answer: t("faq.a6") },
  { question: t("faq.q7"), answer: t("faq.a7") },
  { question: t("faq.q8"), answer: t("faq.a8") },
];

const StyledPress = styled("div", {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  gap: "30px",
  marginTop: "20px",
  padding: "0 20px",
  width: "100%",

  "& img": {
    height: "36px",
    opacity: "1",
    filter: "contrast(1.2)",
    // logos are dark on transparent, so flip them to light on the dark page
    [`.${darkTheme.className} &`]: {
      filter: "invert(1) contrast(1.2)",
    },
    transition: "opacity 0.2s",
    "&:hover": {
      opacity: 0.7,
    },
  },

  "@mobile": {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gridTemplateRows: "1fr 1fr",
    gap: "20px",
    justifyItems: "center",
  },
});

const StyledNotes = styled("div", {
  padding: "20px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
  fontSize: "13px",
  fontWeight: "500",
  width: "320px",
  margin: "0 auto",

  "& p": {
    marginBottom: "10px",
  },

  "& a": {
    transition: "all 0.2s ease",

    "&:hover": {
      opacity: ".6",
      textDecoration: "none",
      transform: "translateY(-1px)",
    },
  },
});

const StyledBillingPage = styled("div", {
  overflowX: "hidden",
  overflowY: "auto",
  paddingTop: "120px",
  paddingBottom: "calc(140px + env(safe-area-inset-bottom))",
  position: "fixed",
  top: 0,
  left: 0,
  width: "100vw",

  // same scrollbar as the rest of the app (see index.css)
  "&::-webkit-scrollbar": {
    width: "0.5rem",
    height: "0.5rem",
  },
  "&::-webkit-scrollbar-track": {
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "$grayA200",
    borderRadius: "0.5rem",
    [`.${darkTheme.className} &`]: {
      backgroundColor: "$whiteA500",
    },
  },

  variants: {
    modalActive: {
      true: {
        overflow: "hidden",
      },
      false: {
        overflowX: "hidden",
        overflowY: "auto",
      },
    },
  },

  "@mobile": {
    paddingTop: "80px",
    paddingLeft: "36px",
    paddingRight: "36px",
    paddingBottom: "100px",
  },

  height: "100vh",
  display: "flex",
  flexDirection: "column",
  backgroundColor: "$wash",
  color: "$text",
  [`.${darkTheme.className} &`]: {
    backgroundColor: "$appBg",
  },

  // toggle uses inline styles, so dark overrides need !important
  [`.${darkTheme.className} & .billing-toggle`]: {
    border: "1px solid $border !important",
  },
  [`.${darkTheme.className} & .billing-toggle-highlight`]: {
    backgroundColor: "$subtleFill !important",
  },
  [`.${darkTheme.className} & .billing-toggle > div`]: {
    color: "$text !important",
  },

  "& .all-plans": {
    display: "flex",
    flexDirection: "row-reverse",
    justifyContent: "center",
    marginTop: "40px",
    "@mobile": {
      flexDirection: "column",
    },
  },

  "& .plan-cursor img": {
    animation: `${spin} 10s linear infinite`,
  },

  "& .plan-details": {
    width: "100%",
    textAlign: "center",
    borderRadius: "32px",
    "@mobile": {
      marginTop: "18px",
    },

    "& h1": {
      fontSize: "36px",
      fontWeight: "normal",
      fontFamily: "GorgeousBaby",
      "& .i": {
        fontStyle: "italic",
      },
    },
    "& h2": {
      fontSize: "24px",
      fontWeight: "400",
      marginTop: "10px",
      marginBottom: "4px",
      lineHeight: "1.1",
    },

    "& h3": {
      fontSize: "12px",
      fontWeight: "normal",
      marginTop: "8px",
      lineHeight: "1.1",
    },

  },

  "& .features": {
    margin: "30px auto",
    fontSize: "12px",
    color: "rgb(0, 0, 0, .8)",
    [`.${darkTheme.className} &`]: {
      color: "rgba(255, 255, 255, 0.8)",
    },
    width: "100%",
    lineHeight: "1rem",
    maxWidth: "600px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",

    "& .feature": {
      width: "100%",
      alignItems: "center",
      marginBottom: "12px",

      "& .feature-line": {
        backgroundColor: "rgb(234, 189, 250, .7)",
        // The 70% alpha reads as washed out over a dark page, so dark mode uses
        // the solid color the light version appears as.
        [`.${darkTheme.className} &`]: {
          backgroundColor: "rgb(238, 206, 249)",
        },
        // fixed pastel background, so text stays dark in dark mode too
        color: "rgb(0, 0, 0, .8)",
        border: "2px solid $border",
        display: "flex",
        borderRadius: "10px",
        padding: "20px",
        flexDirection: "column",
        alignItems: "center",
        width: "100%",
        textAlign: "center",
        gap: "3px",
      },


      "& .feature-header": {
        fontSize: "15px",
        marginBottom: "4px",
        //fontFamily: "KeplerLightCondensed"
        lineHeight: "1.15em",
        fontStyle: "normal",
        fontWeight: "500",
        textTransform: "none",
        color: "#000",
      },

      "& img": {
        maxWidth: "90%",
        borderRadius: "200px 200px 8px 8px",
        border: "0px solid #000",
        marginBottom: "0px",
        filter: "saturate(.8)!important"
      },
      "& .plus": {
        borderRadius: "4px",
        backgroundColor: "$lightPurple",
        color: "black",
        border: "none",
      },
    },
  },

  "& .home-button": {
    backgroundColor: "transparent",
    height: "32px !important",
    padding: "6px 8px !important",
    borderRadius: "10px !important",
    "@mobile": {
      left: "4px !important",
      top: "55px !important",
      backgroundColor: "#f6f6f6",
      [`.${darkTheme.className} &`]: {
        backgroundColor: "$appBg",
      },
    },
  },

  "& .plan": {
    width: "380px",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    paddingTop: "5px",
    paddingLeft: "30px",
    paddingRight: "30px",
    marginLeft: "8px",
    marginRight: "8px",
    
    "& button": {
      width: "220px",
      marginTop: "10px",
      marginBottom: "10px",
      borderRadius: "50px",
      backgroundColor: "#000",
      color: "#fff",
      transition: "all 0.2s ease-in-out",
      boxShadow: "0px 3px 0px 0px #000",
      // primary button is the brand purple with dark text in dark mode
      [`.${darkTheme.className} &`]: {
        backgroundColor: "#efc1ff",
        color: "#000",
        boxShadow: "0px 3px 0px 0px #000",
      },
      "&:hover": {
        boxShadow: "0px 0px 0px 0px #000",
        transform: "translateY(4px)",
        opacity: 0.8,
      },
    },
    
    "@mobile": {
      width: "100%",
      marginLeft: 0,
      marginRight: 0,
      paddingLeft: "10px",
      paddingRight: "10px",
    },
  },

  "& .continue-button": {
    height: "50px !important",
  },

  "& .plan-info": {
    width: "100%",
    paddingLeft: "16px",
    paddingRight: "16px",
    marginBottom: "24px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",

    "& .pricing-box": {
      backgroundColor: "$surface",
      border: "1px solid $border",
      borderRadius: "20px",
      padding: "20px",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      width: "100%",
      maxWidth: "300px",
      "@mobile": {
        maxWidth: "100%",
        width: "100%",
      },
    },

    "& h1": {
      fontSize: "27px",
      fontFamily: "KeplerLightCondensed",
      textAlign: "center",
      fontWeight: "500",
      lineHeight: "1.2",
      width: "300px",
      marginBottom: "8px",
      "@mobile": {
        fontSize: "30px",
      },
    },

    "& h2": {
      fontSize: "14px",
      alignItems: "center",
      fontWeight: "400",
    },

    "& h3": {
      textAlign: "center",
      padding: "5px",
      marginTop: "10px",
      lineHeight: "1.2rem",
      fontSize: "14px",
      fontWeight: "400",

      "@mobile": {
        fontSize: "14px",
      },
    },
    "& p": {
      fontSize: "11px",
      marginTop: "8px",
    },
  },

  ".billing-modal": {
    position: "fixed",
    zIndex: "9999999!important",
    left: 0,
    right: 0,
    top: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(60, 60, 60, 0.25)",
    paddingTop: "0px",
    justifyContent: "center",
    alignItems: "center",
    display: "flex",
    [`.${darkTheme.className} &`]: {
      backgroundColor: "$scrim",
    },
    "& .strong": {
      fontWeight: "500",
      marginRight: "4px",
    },
    "@mobile": {
      backgroundColor: "#f6f6f6",
      [`.${darkTheme.className} &`]: {
        backgroundColor: "$appBg",
      },
    },
    "&.app-store-modal, &.play-store-modal": {
      "@mobile": {
        backgroundColor: "rgba(60, 60, 60, 0.25)",
        [`.${darkTheme.className} &`]: {
          backgroundColor: "$scrim",
        },
      },
    },
  },
});

const StyledAppStoreModal = styled("div", {
  backgroundColor: "$wash",
  color: "$text",
  padding: "40px",
  borderRadius: "24px",
  textAlign: "center",
  width: "400px",
  margin: "0 auto",
  border: "1px solid $border",
  position: "relative",
  [`.${darkTheme.className} &`]: {
    backgroundColor: "$appBg",
  },

  "& .x-button": {
    position: "absolute",
    top: "20px",
    right: "20px",
    width: "20px",
    height: "20px",
    filter: "invert(1)",
    [`.${darkTheme.className} &`]: {
      filter: "none",
    },
    cursor: "pointer",
    opacity: "0.8",
    "&:hover": {
      opacity: "0.5",
    },
  },

  "& h2": {
    fontSize: "16px",
    fontWeight: "500",
    marginBottom: "16px",
  },

  "& p": {
    fontSize: "14px",
    marginBottom: "10px",
    lineHeight: "1.4",
  },

  "@mobile": {
    width: "90%",
  },
});
